import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Stack,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BuildIcon from '@mui/icons-material/Build';
import RemoveIcon from '@mui/icons-material/Remove';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { BaseTableList } from 'components/AgGrid/AgGrid';
import { useAppSelector } from 'store/hook';
import { selectTireInventory } from 'store/tire-inventory.slice';
import TireInventoryHandler from 'handlers/tire-inventory.handler';
import { isDarkTheme } from 'utils/theme';
import { convertStdDateString } from 'utils/helpers/dateTime';
import { type ColDef } from 'ag-grid-community';
import { type TireInventory } from 'types/tire-inventory.types';
import { ThemePalette } from 'mui.theme';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import { CreateTireInventoryModal } from './components/CreateTireInventoryModal';
import { GridSearchIcon } from '@mui/x-data-grid';
import { InstallTireModal } from './components/InstallTireModal';
import { UninstallTireModal } from './components/UninstallTireModal';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const TireInventoryView = () => {
  const { t } = useTranslation();
  const theme = useAppSelector((state) => state.authReducer.customer.theme);
  const customerId = useAppSelector(
    (state) => state.persistedReducer.customer.id
  );
  const enableDarkTheme = isDarkTheme(theme);
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const { tireInventory, isLoading, total, totalPages } =
    useAppSelector(selectTireInventory);
  const assets = useAppSelector((state) => state.assetReducer.assets);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isInstallModalOpen, setIsInstallModalOpen] = useState(false);
  const [isUninstallModalOpen, setIsUninstallModalOpen] = useState(false);
  const [selectedTireForInstall, setSelectedTireForInstall] =
    useState<string>('');
  const [selectedTireForUninstall, setSelectedTireForUninstall] = useState<{
    tireSerialNumber: string;
    installationId: string;
    assetName: string;
    position: string;
  } | null>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');

  const tireInventoryHandler = new TireInventoryHandler();

  // Debounce search keyword
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedKeyword(searchKeyword);
      setCurrentPage(1); // Reset to first page on new search
    }, 500); // 500ms debounce

    return () => {
      clearTimeout(timer);
    };
  }, [searchKeyword]);

  useEffect(() => {
    void tireInventoryHandler.getTireInventory({
      customerId,
      page: currentPage,
      limit: pageSize,
      keyword: debouncedKeyword || undefined,
    });
  }, [customerId, currentPage, pageSize, debouncedKeyword]);

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const handlePageSizeChange = (event: any) => {
    setPageSize(event.target.value as number);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handleSearchChange = (event: any) => {
    setSearchKeyword(event.target.value);
  };

  const handleImportSpreadsheet = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset input value to allow uploading same file again
    event.target.value = '';

    const success = await tireInventoryHandler.importTireInventory(
      file,
      customerId
    );

    if (success) {
      setCurrentPage(1);
      // Reload inventory
      void tireInventoryHandler.getTireInventory({
        customerId,
        page: 1,
        limit: pageSize,
        keyword: debouncedKeyword || undefined,
      });
    }
  };

  const handleCreateSuccess = async () => {
    // Refresh the tire inventory list after creating a new tire
    await tireInventoryHandler.getTireInventory({
      customerId,
      page: currentPage,
      limit: pageSize,
      keyword: debouncedKeyword || undefined,
    });
  };

  const handleInstallClick = (tireSerialNumber: string) => {
    setSelectedTireForInstall(tireSerialNumber);
    setIsInstallModalOpen(true);
  };

  const handleInstallSuccess = async () => {
    // Refresh the tire inventory list after installing a tire
    await tireInventoryHandler.getTireInventory({
      customerId,
      page: currentPage,
      limit: pageSize,
      keyword: debouncedKeyword || undefined,
    });
  };

  const handleUninstallClick = (tireData: any) => {
    const asset = assets.find(
      (a) => a.assetId === tireData.installedOn.assetId
    );
    const assetName = asset ? asset.bumperNumber : t('unknown_asset');

    setSelectedTireForUninstall({
      tireSerialNumber: tireData.tireSerialNumber,
      installationId: tireData.installedOn.installationId,
      assetName,
      position: tireData.installedOn.position,
    });
    setIsUninstallModalOpen(true);
  };

  const handleUninstallSuccess = async () => {
    // Refresh the tire inventory list after uninstalling a tire
    await tireInventoryHandler.getTireInventory({
      customerId,
      page: currentPage,
      limit: pageSize,
      keyword: debouncedKeyword || undefined,
    });
  };

  // Status badge renderer
  const StatusRenderer = (props: any) => {
    const status = props.value as string;
    const getStatusColor = (status: string) => {
      switch (status) {
        case 'IN_STORAGE':
          return 'primary';
        case 'IN_SERVICE':
          return 'success';
        case 'AT_RETREAD':
          return 'warning';
        case 'AT_REPAIR':
          return 'warning';
        case 'SCRAPPED':
          return 'error';
        default:
          return 'default';
      }
    };

    const formatStatus = (status: string) => {
      return status.replace(/_/g, ' ');
    };

    return (
      <Chip
        label={formatStatus(status)}
        color={getStatusColor(status)}
        size="small"
        sx={{ fontWeight: 600 }}
      />
    );
  };

  // Date formatter
  const DateRenderer = (props: any) => {
    if (!props.value) return <span>--</span>;
    return <span>{convertStdDateString(props.value, 'MMM DD, YYYY')}</span>;
  };

  // Tread depth renderer
  const TreadDepthRenderer = (props: any) => {
    if (!props.value) return <span>--</span>;
    return <span>{props.value} mm</span>;
  };

  // Installed On renderer
  const InstalledOnRenderer = (props: any) => {
    const installedOn = props.data.installedOn;

    if (!installedOn) {
      return <span style={{ opacity: 0.5 }}>--</span>;
    }

    const asset = assets.find((a) => a.assetId === installedOn.assetId);
    const assetName = asset ? asset.bumperNumber : t('unknown_asset');

    return (
      <>
        {assetName} - {installedOn.position}
      </>
    );
  };

  // Actions renderer
  const ActionsRenderer = (props: any) => {
    const tireSerialNumber = props.data.tireSerialNumber;
    const currentStatus = props.data.currentStatus;
    const isInService = currentStatus === 'IN_SERVICE';
    const isInStorage = currentStatus === 'IN_STORAGE';
    const canInstall = isInStorage && !isInService;

    return (
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
        {/* Install Button */}
        <Tooltip
          title={
            isInService
              ? t('already_installed_on_asset')
              : !isInStorage
              ? t('tire_must_be_in_storage_to_install')
              : t('install_tire_to_asset')
          }
        >
          <span>
            <IconButton
              size="small"
              disabled={!canInstall}
              onClick={() => {
                handleInstallClick(tireSerialNumber);
              }}
              sx={{
                color: !canInstall
                  ? enableDarkTheme
                    ? 'rgba(255, 255, 255, 0.3)'
                    : 'rgba(0, 0, 0, 0.26)'
                  : ThemePalette.typography.primary,
                '&:hover': {
                  backgroundColor: enableDarkTheme
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.04)',
                },
                '&.Mui-disabled': {
                  color: enableDarkTheme
                    ? 'rgba(255, 255, 255, 0.3)'
                    : 'rgba(0, 0, 0, 0.26)',
                },
              }}
            >
              <BuildIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        {/* Uninstall Button */}
        <Tooltip
          title={
            isInService
              ? t('uninstall_tire_from_asset')
              : t('tire_not_installed')
          }
        >
          <span>
            <IconButton
              size="small"
              disabled={!isInService}
              onClick={() => {
                handleUninstallClick(props.data);
              }}
              sx={{
                color: !isInService
                  ? enableDarkTheme
                    ? 'rgba(255, 255, 255, 0.3)'
                    : 'rgba(0, 0, 0, 0.26)'
                  : '#d32f2f', // Red color for uninstall
                '&:hover': {
                  backgroundColor: enableDarkTheme
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.04)',
                },
                '&.Mui-disabled': {
                  color: enableDarkTheme
                    ? 'rgba(255, 255, 255, 0.3)'
                    : 'rgba(0, 0, 0, 0.26)',
                },
              }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  };

  // Column definitions
  const columnDefs: Array<ColDef<TireInventory>> = [
    {
      field: 'tireSerialNumber',
      headerName: t('serial_number_2'),
      minWidth: 220,
      // pinned: 'left',
      cellStyle: {
        fontWeight: 600,
        color: enableDarkTheme
          ? ThemePalette.typography.white
          : ThemePalette.typography.primary,
      },
    },
    {
      field: 'tireManufacturer',
      headerName: t('manufacturer'),
      minWidth: 150,
    },
    {
      field: 'tireModel',
      headerName: t('model_10'),
      minWidth: 120,
    },
    {
      field: 'tireSize',
      headerName: t('size'),
      minWidth: 140,
    },
    {
      field: 'initialTreadDepth',
      headerName: t('tread_depth'),
      minWidth: 130,
      cellRenderer: TreadDepthRenderer,
    },
    {
      field: 'maxLoadIndex',
      headerName: t('load_index'),
      minWidth: 120,
    },
    {
      field: 'tireMfdDate',
      headerName: t('manufacture_date'),
      minWidth: 160,
      cellRenderer: DateRenderer,
    },
    {
      field: 'tireExpiryDate',
      headerName: t('expiry_date'),
      minWidth: 160,
      cellRenderer: DateRenderer,
    },
    {
      field: 'currentStatus',
      headerName: t('cdp_step_status'),
      minWidth: 150,
      cellRenderer: StatusRenderer,
    },
    {
      field: 'installedOn',
      headerName: t('installed_on_2'),
      minWidth: 220,
      cellRenderer: InstalledOnRenderer,
    },
    {
      field: 'createdAt',
      headerName: t('created_at'),
      minWidth: 160,
      cellRenderer: DateRenderer,
    },
    {
      headerName: t('actions'),
      minWidth: 150,
      maxWidth: 150,
      pinned: 'right',
      cellRenderer: ActionsRenderer,
      sortable: false,
      filter: false,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
    },
  ];

  return (
    <CustomMuiThemeProvider>
      <Box sx={{ padding: '20px' }}>
        {/* Header Section */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '20px',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: '250px' }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                marginBottom: '8px',
              }}
            >
              {t('tire_inventory')}
            </Typography>
            <Typography variant="body2">
              {isLoading ? (
                t('loading_ellipsis')
              ) : (
                <>
                  {t('showing_tires_of_total', {
                    shown: tireInventory.length,
                    total,
                  })}
                  {totalPages > 1 &&
                    ` • ${t('page_of_total', {
                      current: currentPage,
                      total: totalPages,
                    })}`}
                  {debouncedKeyword &&
                    ` • ${t('filtered_by_keyword', {
                      keyword: debouncedKeyword,
                    })}`}
                </>
              )}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            {/* Search Input */}
            <TextField
              size="small"
              placeholder={t('search_tires')}
              value={searchKeyword}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <GridSearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: '250px',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: enableDarkTheme
                    ? 'rgba(255,255,255,0.05)'
                    : 'rgba(0,0,0,0.02)',
                },
              }}
            />
            {/* Create Button */}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setIsCreateModalOpen(true);
              }}
              sx={{
                backgroundColor: ThemePalette.typography.primary,
                '&:hover': {
                  backgroundColor: ThemePalette.typography.primary,
                  opacity: 0.9,
                },
                fontWeight: 600,
              }}
            >
              {t('add_tire')}
            </Button>

            {/* Import Button */}
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFileIcon />}
              sx={{
                borderColor: ThemePalette.typography.primary,
                color: ThemePalette.typography.primary,
                '&:hover': {
                  borderColor: ThemePalette.typography.primary,
                  backgroundColor: `${ThemePalette.typography.primary}11`,
                },
                fontWeight: 600,
              }}
            >
              {t('import_excel_csv') || 'Import Excel/CSV'}
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                hidden
                onChange={handleImportSpreadsheet}
              />
            </Button>

            {/* Page Size Selector */}
            <FormControl
              size="small"
              sx={{
                minWidth: 120,
              }}
            >
              <InputLabel>{t('rows_per_page')}</InputLabel>
              <Select
                value={pageSize}
                label={t('rows_per_page')}
                onChange={handlePageSizeChange}
                sx={{
                  color: palette.textPrimary,
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: enableDarkTheme
                      ? ThemePalette.typography.grey
                      : palette.borderGrey,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: enableDarkTheme
                      ? ThemePalette.typography.white
                      : ThemePalette.typography.primary,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: ThemePalette.typography.primary,
                  },
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Table Section */}
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '400px',
            }}
          >
            <CircularProgress />
          </Box>
        ) : tireInventory.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '400px',
              backgroundColor: enableDarkTheme
                ? palette.boxBackground
                : palette.boxBackground,
              borderRadius: '8px',
              border: `1px dashed ${
                enableDarkTheme
                  ? ThemePalette.typography.grey
                  : ThemePalette.typography.darkGrey
              }`,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: enableDarkTheme
                  ? ThemePalette.typography.grey
                  : ThemePalette.typography.darkGrey,
                marginBottom: '8px',
              }}
            >
              {t('no_tire_inventory_found')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: enableDarkTheme
                  ? ThemePalette.typography.grey
                  : ThemePalette.typography.darkGrey,
              }}
            >
              {t('there_are_no_tires_in_the_inventory_at_this_time')}
            </Typography>
          </Box>
        ) : (
          <>
            <BaseTableList
              rowData={tireInventory}
              columnDefs={columnDefs}
              pagination={false}
              tableStyles={{
                height: `55vh`,
                borderRadius: '8px',
              }}
              getRowId={(params: any) => params.data.tireSerialNumber}
            />

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <Stack
                spacing={2}
                sx={{
                  marginTop: '20px',
                  alignItems: 'center',
                  paddingBottom: '20px',
                }}
              >
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: palette.textPrimary,
                      borderColor: enableDarkTheme
                        ? ThemePalette.typography.grey
                        : palette.borderGrey,
                    },
                    '& .Mui-selected': {
                      backgroundColor: `${ThemePalette.typography.primary} !important`,
                      color: 'white !important',
                    },
                  }}
                />
              </Stack>
            )}
          </>
        )}
      </Box>

      {/* Create Tire Inventory Modal */}
      {isCreateModalOpen && (
        <CreateTireInventoryModal
          title={t('add_new_tire_to_inventory')}
          open={isCreateModalOpen}
          handleAbort={() => {
            setIsCreateModalOpen(false);
          }}
          enableDarkTheme={enableDarkTheme}
          onSuccess={handleCreateSuccess}
        />
      )}

      {/* Install Tire Modal */}
      {isInstallModalOpen && (
        <InstallTireModal
          tireSerialNumber={selectedTireForInstall}
          open={isInstallModalOpen}
          handleAbort={() => {
            setIsInstallModalOpen(false);
          }}
          enableDarkTheme={enableDarkTheme}
          onSuccess={handleInstallSuccess}
        />
      )}

      {/* Uninstall Tire Modal */}
      {isUninstallModalOpen && selectedTireForUninstall && (
        <UninstallTireModal
          tireSerialNumber={selectedTireForUninstall.tireSerialNumber}
          installationId={selectedTireForUninstall.installationId}
          assetName={selectedTireForUninstall.assetName}
          position={selectedTireForUninstall.position}
          open={isUninstallModalOpen}
          handleAbort={() => {
            setIsUninstallModalOpen(false);
            setSelectedTireForUninstall(null);
          }}
          enableDarkTheme={enableDarkTheme}
          onSuccess={handleUninstallSuccess}
        />
      )}
    </CustomMuiThemeProvider>
  );
};

export default TireInventoryView;
